/**
 * SocialPage — Symphony Social Media Publishing Hub
 * Connect channels, compose posts, publish campaign content.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  BarChart2,
  CheckCircle2,
  ChevronRight,
  ClipboardCopy,
  Facebook,
  Instagram,
  Linkedin,
  Loader2,
  RefreshCw,
  Send,
  Settings,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

import { cn } from "@/lib/utils";
import { toast } from "sonner";

// ── Types ──────────────────────────────────────────────────────────────────────

type PlatformId = "x" | "linkedin" | "facebook" | "instagram" | "google_ads";

interface ChannelStatus {
  platform: PlatformId;
  label: string;
  connected: boolean;
  missing_keys: string[];
}

interface CredentialField {
  key: string;
  label: string;
  placeholder?: string;
}

interface PlatformMeta {
  id: PlatformId;
  label: string;
  charLimit: number | null;
  iconBg: string;
  credFields: CredentialField[];
  publishable: boolean;
}

interface PublishResult {
  platform: PlatformId;
  label: string;
  ok: boolean;
  postUrl?: string;
  error?: string;
  structured?: string; // for google_ads
}

interface HistoryEntry {
  id: string;
  ts: Date;
  platform: PlatformId;
  label: string;
  preview: string;
  ok: boolean;
  postId?: string;
  postUrl?: string;
}

// ── Platform definitions ───────────────────────────────────────────────────────

const PLATFORMS: PlatformMeta[] = [
  {
    id: "x",
    label: "X (Twitter)",
    charLimit: 280,
    iconBg: "bg-zinc-900 text-white",
    publishable: true,
    credFields: [
      { key: "x_api_key", label: "API Key" },
      { key: "x_api_secret", label: "API Secret" },
      { key: "x_access_token", label: "Access Token" },
      { key: "x_access_token_secret", label: "Access Token Secret" },
    ],
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    charLimit: 1300,
    iconBg: "bg-blue-700 text-white",
    publishable: true,
    credFields: [
      { key: "linkedin_access_token", label: "Access Token" },
      {
        key: "linkedin_author_urn",
        label: "Author URN",
        placeholder: "urn:li:person:XXXXX",
      },
    ],
  },
  {
    id: "facebook",
    label: "Facebook",
    charLimit: 2200,
    iconBg: "bg-blue-600 text-white",
    publishable: true,
    credFields: [
      { key: "facebook_page_id", label: "Page ID" },
      { key: "facebook_page_access_token", label: "Page Access Token" },
    ],
  },
  {
    id: "instagram",
    label: "Instagram",
    charLimit: 2200,
    iconBg: "bg-gradient-to-br from-purple-600 to-rose-500 text-white",
    publishable: true,
    credFields: [
      { key: "instagram_user_id", label: "IG User ID" },
      { key: "instagram_page_access_token", label: "Page Access Token" },
    ],
  },
  {
    id: "google_ads",
    label: "Google Ads",
    charLimit: null,
    iconBg: "bg-amber-500 text-white",
    publishable: false,
    credFields: [],
  },
];

const platformById = (id: PlatformId): PlatformMeta =>
  PLATFORMS.find((p) => p.id === id)!;

// ── Platform icon ──────────────────────────────────────────────────────────────

function PlatformIcon({
  id,
  className,
}: {
  id: PlatformId;
  className?: string;
}) {
  const base = cn("flex items-center justify-center w-8 h-8 rounded-md text-white flex-shrink-0", className);
  switch (id) {
    case "x":
      return (
        <div className={cn(base, "bg-zinc-900")}>
          <span className="font-black text-sm leading-none">𝕏</span>
        </div>
      );
    case "linkedin":
      return (
        <div className={cn(base, "bg-blue-700")}>
          <Linkedin className="w-4 h-4" />
        </div>
      );
    case "facebook":
      return (
        <div className={cn(base, "bg-blue-600")}>
          <Facebook className="w-4 h-4" />
        </div>
      );
    case "instagram":
      return (
        <div className={cn(base, "bg-gradient-to-br from-purple-600 to-rose-500")}>
          <Instagram className="w-4 h-4" />
        </div>
      );
    case "google_ads":
      return (
        <div className={cn(base, "bg-amber-500")}>
          <BarChart2 className="w-4 h-4" />
        </div>
      );
  }
}

// ── Channel card ───────────────────────────────────────────────────────────────

function ChannelCard({
  status,
  onConfigure,
}: {
  status: ChannelStatus;
  onConfigure: (platform: PlatformId) => void;
}) {
  const meta = platformById(status.platform);

  return (
    <div className="flex items-start gap-3 rounded-lg border border-zinc-700/50 bg-zinc-800/50 p-3">
      <PlatformIcon id={status.platform} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-100">{meta.label}</span>
          {status.connected ? (
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs px-1.5 py-0">
              <span className="mr-1">●</span>Connected
            </Badge>
          ) : (
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs px-1.5 py-0">
              <span className="mr-1">○</span>Not configured
            </Badge>
          )}
        </div>
        {!status.connected && status.missing_keys.length > 0 && (
          <p className="mt-0.5 text-[11px] text-zinc-500 truncate">
            Missing: {status.missing_keys.join(", ")}
          </p>
        )}
        {status.platform === "google_ads" && (
          <p className="mt-0.5 text-[11px] text-zinc-500">
            Copy generated — import via Google Ads Editor
          </p>
        )}
      </div>
      {meta.credFields.length > 0 && (
        <Button
          size="sm"
          variant="ghost"
          className="flex-shrink-0 h-7 px-2 text-xs text-zinc-400 hover:text-zinc-100"
          onClick={() => onConfigure(status.platform)}
        >
          <Settings className="w-3 h-3 mr-1" />
          Configure
        </Button>
      )}
    </div>
  );
}

// ── Credential sheet ───────────────────────────────────────────────────────────

function CredentialSheet({
  platform,
  open,
  onClose,
  onSaved,
}: {
  platform: PlatformId | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const meta = platform ? platformById(platform) : null;
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValues({});
  }, [open, platform]);

  const handleSave = useCallback(async () => {
    if (!meta) return;
    setSaving(true);
    try {
      await Promise.all(
        meta.credFields.map(({ key }) =>
          fetch(`/api/settings/${key}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: values[key] ?? "" }),
          }).then((r) => {
            if (!r.ok) throw new Error(`Failed to save ${key}`);
          })
        )
      );
      toast.success(`${meta.label} credentials saved`);
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [meta, values, onSaved, onClose]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="bg-zinc-900 border-zinc-700 text-zinc-100 w-[360px]">
        <SheetHeader>
          <SheetTitle className="text-zinc-100 flex items-center gap-2">
            {platform && <PlatformIcon id={platform} className="w-6 h-6" />}
            {meta?.label} — Credentials
          </SheetTitle>
        </SheetHeader>

        {meta && (
          <div className="mt-6 flex flex-col gap-4">
            {meta.credFields.map(({ key, label, placeholder }) => (
              <div key={key} className="flex flex-col gap-1.5">
                <label className="text-xs text-zinc-400 font-medium">
                  {label}
                </label>
                <Input
                  type="password"
                  placeholder={placeholder ?? `Enter ${label}`}
                  className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500 text-sm"
                  value={values[key] ?? ""}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                />
              </div>
            ))}

            <Button
              onClick={handleSave}
              disabled={saving}
              className="mt-2 bg-violet-600 hover:bg-violet-500 text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Save Credentials
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ── Channel panel ──────────────────────────────────────────────────────────────

function ChannelPanel({
  channels,
  loading,
  onRefresh,
}: {
  channels: ChannelStatus[];
  loading: boolean;
  onRefresh: () => void;
}) {
  const [configuring, setConfiguring] = useState<PlatformId | null>(null);

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">
            Channels
          </h2>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-zinc-400 hover:text-zinc-100"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
          </Button>
        </div>

        <div className="flex flex-col gap-2">
          {PLATFORMS.map((meta) => {
            const status = channels.find((c) => c.platform === meta.id) ?? {
              platform: meta.id,
              label: meta.label,
              connected: false,
              missing_keys: meta.credFields.map((f) => f.key),
            };
            return (
              <ChannelCard
                key={meta.id}
                status={status}
                onConfigure={setConfiguring}
              />
            );
          })}
        </div>

        <div className="mt-4 rounded-md border border-zinc-700/30 bg-zinc-800/30 p-3">
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Credentials are stored encrypted in Symphony settings. Connections
            are validated on each publish attempt.
          </p>
        </div>
      </div>

      <CredentialSheet
        platform={configuring}
        open={configuring !== null}
        onClose={() => setConfiguring(null)}
        onSaved={onRefresh}
      />
    </>
  );
}

// ── Char counter badge ─────────────────────────────────────────────────────────

function CharCounter({
  text,
  platform,
}: {
  text: string;
  platform: PlatformId;
}) {
  const meta = platformById(platform);
  if (!meta.charLimit) return null;
  const len = text.length;
  const over = len > meta.charLimit;
  return (
    <span
      className={cn(
        "text-[11px] font-mono tabular-nums",
        over ? "text-red-400" : "text-zinc-500"
      )}
    >
      {len}/{meta.charLimit}
    </span>
  );
}

// ── Post preview card ──────────────────────────────────────────────────────────

function PostPreview({
  platform,
  text,
  imageUrl,
}: {
  platform: PlatformId;
  text: string;
  imageUrl: string;
}) {
  const meta = platformById(platform);
  const isOver = meta.charLimit !== null && text.length > meta.charLimit;
  const displayText = isOver ? text.slice(0, meta.charLimit!) + "…" : text;

  return (
    <div className="rounded-lg border border-zinc-700/50 bg-zinc-800/60 p-3 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <PlatformIcon id={platform} className="w-6 h-6" />
        <span className="text-xs font-medium text-zinc-400">{meta.label}</span>
        {isOver && (
          <Badge className="ml-auto bg-red-500/20 text-red-400 border-red-500/30 text-[10px] px-1">
            Over limit
          </Badge>
        )}
      </div>
      {imageUrl && platform === "instagram" && (
        <div className="rounded overflow-hidden bg-zinc-700 h-24 flex items-center justify-center text-xs text-zinc-500">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            "No image"
          )}
        </div>
      )}
      <p className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed">
        {displayText || (
          <span className="text-zinc-600 italic">Post content appears here…</span>
        )}
      </p>
    </div>
  );
}

// ── Quick Compose tab ──────────────────────────────────────────────────────────

const PUBLISHABLE_PLATFORMS: PlatformId[] = ["x", "linkedin", "facebook", "instagram"];

function QuickComposeTab({
  connectedChannels,
  onPublished,
  initialContent,
  initialPlatform,
  onConsumedInitial,
}: {
  connectedChannels: Set<PlatformId>;
  onPublished: (entries: HistoryEntry[]) => void;
  initialContent: string;
  initialPlatform: PlatformId | null;
  onConsumedInitial: () => void;
}) {
  const [selected, setSelected] = useState<Set<PlatformId>>(new Set());
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState<PublishResult[]>([]);
  const initialConsumed = useRef(false);

  // Pre-fill from campaign
  useEffect(() => {
    if (initialContent && initialPlatform && !initialConsumed.current) {
      initialConsumed.current = true;
      setText(initialContent);
      if (connectedChannels.has(initialPlatform)) {
        setSelected(new Set([initialPlatform]));
      }
      onConsumedInitial();
    }
  }, [initialContent, initialPlatform, connectedChannels, onConsumedInitial]);

  const togglePlatform = (id: PlatformId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handlePublish = useCallback(async () => {
    if (selected.size === 0 || !text.trim()) return;
    setPublishing(true);
    setResults([]);
    const newEntries: HistoryEntry[] = [];

    for (const platformId of selected) {
      const meta = platformById(platformId);
      try {
        const body: Record<string, unknown> = { platform: platformId, text };
        if (platformId === "instagram" && imageUrl) {
          body.image_url = imageUrl;
        }
        const res = await fetch("/api/social/publish", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data: unknown = await res.json();
        const typed = data as Record<string, unknown>;
        const ok = res.ok;
        const postUrl = typeof typed.url === "string" ? typed.url : undefined;
        const structured =
          typeof typed.structured === "string" ? typed.structured : undefined;
        const errorMsg = typeof typed.error === "string" ? typed.error : undefined;

        setResults((prev) => [
          ...prev,
          {
            platform: platformId,
            label: meta.label,
            ok,
            postUrl,
            structured,
            error: ok ? undefined : errorMsg ?? "Unknown error",
          },
        ]);

        newEntries.push({
          id: crypto.randomUUID(),
          ts: new Date(),
          platform: platformId,
          label: meta.label,
          preview: text.slice(0, 80),
          ok,
          postUrl,
        });
      } catch {
        const errorResult: PublishResult = {
          platform: platformId,
          label: meta.label,
          ok: false,
          error: "Network error",
        };
        setResults((prev) => [...prev, errorResult]);
        newEntries.push({
          id: crypto.randomUUID(),
          ts: new Date(),
          platform: platformId,
          label: meta.label,
          preview: text.slice(0, 80),
          ok: false,
        });
      }
    }

    onPublished(newEntries);
    setPublishing(false);
  }, [selected, text, imageUrl, onPublished]);

  const connectedPublishable = PUBLISHABLE_PLATFORMS.filter((p) =>
    connectedChannels.has(p)
  );

  const instagramSelected = selected.has("instagram");

  return (
    <div className="flex flex-col gap-4">
      {/* Channel selector */}
      <div>
        <p className="text-xs text-zinc-400 mb-2 font-medium">
          Post to channels
        </p>
        {connectedPublishable.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">
            No channels connected — configure them in the left panel.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {connectedPublishable.map((id) => {
              const meta = platformById(id);
              const active = selected.has(id);
              return (
                <button
                  key={id}
                  onClick={() => togglePlatform(id)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                    active
                      ? "border-violet-500 bg-violet-500/20 text-violet-300"
                      : "border-zinc-600 bg-zinc-800 text-zinc-400 hover:border-zinc-500"
                  )}
                >
                  <PlatformIcon id={id} className="w-4 h-4 rounded-sm" />
                  {meta.label}
                  {active && <CheckCircle2 className="w-3 h-3 ml-0.5" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Composer + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="flex flex-col gap-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write your post…"
            rows={7}
            className="w-full rounded-md border border-zinc-700 bg-zinc-800 text-zinc-100 placeholder:text-zinc-500 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500"
          />

          {/* Per-platform char counters */}
          {selected.size > 0 && (
            <div className="flex flex-wrap gap-3">
              {Array.from(selected).map((id) => (
                <div key={id} className="flex items-center gap-1.5">
                  <PlatformIcon id={id} className="w-4 h-4 rounded-sm" />
                  <CharCounter text={text} platform={id} />
                </div>
              ))}
            </div>
          )}

          {/* Instagram image URL */}
          {instagramSelected && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-zinc-400 font-medium">
                Image URL <span className="text-rose-400">*</span> (required for Instagram)
              </label>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://…"
                className="bg-zinc-800 border-zinc-600 text-zinc-100 placeholder:text-zinc-500 text-sm"
              />
            </div>
          )}

          <Button
            onClick={handlePublish}
            disabled={publishing || selected.size === 0 || !text.trim()}
            className="bg-violet-600 hover:bg-violet-500 text-white self-start"
          >
            {publishing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Publish to {selected.size} channel{selected.size !== 1 ? "s" : ""}
          </Button>
        </div>

        {/* Preview pane */}
        <div className="flex flex-col gap-2">
          <p className="text-xs text-zinc-400 font-medium">Preview</p>
          {selected.size === 0 ? (
            <p className="text-sm text-zinc-600 italic">
              Select channels to preview
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
              {Array.from(selected).map((id) => (
                <PostPreview
                  key={id}
                  platform={id}
                  text={text}
                  imageUrl={imageUrl}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Publish results */}
      {results.length > 0 && (
        <div className="flex flex-col gap-2 mt-2">
          <p className="text-xs text-zinc-400 font-medium uppercase tracking-wider">
            Results
          </p>
          {results.map((r) => (
            <PublishResultRow key={r.platform} result={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function PublishResultRow({ result }: { result: PublishResult }) {
  const [copied, setCopied] = useState(false);

  const copyStructured = () => {
    if (!result.structured) return;
    navigator.clipboard.writeText(result.structured).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md border px-3 py-2 text-sm",
        result.ok
          ? "border-emerald-500/30 bg-emerald-500/10"
          : "border-red-500/30 bg-red-500/10"
      )}
    >
      {result.ok ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <span className="font-medium text-zinc-200">{result.label}</span>
        {result.ok && result.postUrl && (
          <a
            href={result.postUrl}
            target="_blank"
            rel="noreferrer"
            className="ml-2 text-xs text-violet-400 underline underline-offset-2 hover:text-violet-300"
          >
            View post <ChevronRight className="inline w-3 h-3" />
          </a>
        )}
        {result.ok && result.structured && (
          <div className="mt-2">
            <pre className="text-[11px] text-zinc-400 bg-zinc-900 rounded p-2 overflow-x-auto whitespace-pre-wrap">
              {result.structured}
            </pre>
            <Button
              size="sm"
              variant="ghost"
              className="mt-1 h-6 text-xs text-zinc-400 hover:text-zinc-100 px-2"
              onClick={copyStructured}
            >
              <ClipboardCopy className="w-3 h-3 mr-1" />
              {copied ? "Copied!" : "Copy RSA copy"}
            </Button>
          </div>
        )}
        {!result.ok && result.error && (
          <p className="text-xs text-red-400 mt-0.5">{result.error}</p>
        )}
      </div>
    </div>
  );
}

// ── Campaign Posts tab ─────────────────────────────────────────────────────────

interface CampaignSection {
  id: string;
  type: "email" | "linkedin" | "x" | "google_ads";
  label: string;
  content: string;
}

function CampaignPostsTab({
  onSendToCompose,
}: {
  onSendToCompose: (content: string, platform: PlatformId) => void;
}) {
  const [raw, setRaw] = useState("");
  const [sections, setSections] = useState<CampaignSection[]>([]);
  const [trimmingId, setTrimmingId] = useState<string | null>(null);

  const parseSections = useCallback((input: string): CampaignSection[] => {
    const result: CampaignSection[] = [];

    const emailMatch = input.match(/[Ee]mail[:\s]*\n([\s\S]*?)(?=\n[A-Z]|\n##|$)/);
    if (emailMatch) {
      result.push({
        id: "email",
        type: "email",
        label: "Email",
        content: emailMatch[1].trim(),
      });
    }

    const liMatch = input.match(/[Ll]inked[Ii]n[:\s]*\n([\s\S]*?)(?=\n[A-Z]|\n##|$)/);
    if (liMatch) {
      result.push({
        id: "linkedin",
        type: "linkedin",
        label: "LinkedIn Post",
        content: liMatch[1].trim(),
      });
    }

    const xMatch = input.match(/[Xx](?: \(Twitter\))?[:\s]*\n([\s\S]*?)(?=\n[A-Z]|\n##|$)/);
    if (xMatch) {
      result.push({
        id: "x",
        type: "x",
        label: "X (Twitter) Post",
        content: xMatch[1].trim(),
      });
    }

    const adsMatch = input.match(/[Gg]oogle\s+[Aa]ds[:\s]*\n([\s\S]*?)(?=\n[A-Z]|\n##|$)/);
    if (adsMatch) {
      result.push({
        id: "google_ads",
        type: "google_ads",
        label: "Google Ads RSA",
        content: adsMatch[1].trim(),
      });
    }

    // If nothing matched, treat everything as LinkedIn
    if (result.length === 0 && input.trim()) {
      result.push({
        id: "linkedin",
        type: "linkedin",
        label: "LinkedIn Post",
        content: input.trim(),
      });
    }

    return result;
  }, []);

  const handleParse = () => {
    const parsed = parseSections(raw);
    setSections(parsed);
    if (parsed.length > 0) {
      toast.success(`Parsed ${parsed.length} section${parsed.length > 1 ? "s" : ""}`);
    }
  };

  const handleTrim = (sectionId: string) => {
    setTrimmingId(sectionId);
    setTimeout(() => {
      setSections((prev) =>
        prev.map((s) => {
          if (s.id !== sectionId) return s;
          const trimmed = s.content.slice(0, 277) + "…";
          return { ...s, content: trimmed };
        })
      );
      setTrimmingId(null);
      toast.success("Post trimmed to 280 characters");
    }, 600);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="text-xs text-zinc-400 font-medium">
          Paste campaign content (from Campaign Builder or any source)
        </label>
        <textarea
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={`Email:\nYour email body here...\n\nLinkedIn:\nYour LinkedIn post...\n\nX:\nYour tweet...\n\nGoogle Ads:\nHeadlines and descriptions...`}
          rows={8}
          className="w-full rounded-md border border-zinc-700 bg-zinc-800 text-zinc-100 placeholder:text-zinc-500 text-sm px-3 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500 font-mono"
        />
        <Button
          onClick={handleParse}
          disabled={!raw.trim()}
          className="self-start bg-zinc-700 hover:bg-zinc-600 text-zinc-100"
          size="sm"
        >
          Parse Sections
        </Button>
      </div>

      {sections.length > 0 && (
        <div className="flex flex-col gap-3">
          {sections.map((section) => (
            <CampaignSectionCard
              key={section.id}
              section={section}
              trimming={trimmingId === section.id}
              onTrim={() => handleTrim(section.id)}
              onPublish={() => {
                const platformMap: Record<CampaignSection["type"], PlatformId | null> = {
                  email: null,
                  linkedin: "linkedin",
                  x: "x",
                  google_ads: "google_ads",
                };
                const platform = platformMap[section.type];
                if (platform) {
                  onSendToCompose(section.content, platform);
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignSectionCard({
  section,
  trimming,
  onTrim,
  onPublish,
}: {
  section: CampaignSection;
  trimming: boolean;
  onTrim: () => void;
  onPublish: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const isEmail = section.type === "email";
  const isX = section.type === "x";
  const isGoogleAds = section.type === "google_ads";
  const isOverXLimit = isX && section.content.length > 280;

  const copy = () => {
    navigator.clipboard.writeText(section.content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const platformColors: Record<CampaignSection["type"], string> = {
    email: "bg-zinc-700/40 border-zinc-600/40",
    linkedin: "bg-blue-900/20 border-blue-700/30",
    x: "bg-zinc-900/50 border-zinc-600/40",
    google_ads: "bg-amber-900/20 border-amber-700/30",
  };

  return (
    <div
      className={cn(
        "rounded-lg border p-4 flex flex-col gap-3",
        platformColors[section.type]
      )}
    >
      <div className="flex items-center gap-2 flex-wrap">
        {section.type !== "email" && (
          <PlatformIcon
            id={section.type as PlatformId}
            className="w-5 h-5 rounded-sm"
          />
        )}
        <span className="text-sm font-semibold text-zinc-200">
          {section.label}
        </span>
        {isEmail && (
          <Badge className="bg-zinc-600/50 text-zinc-400 border-zinc-500/40 text-[10px]">
            Not publishable — use email client
          </Badge>
        )}
        {isX && (
          <span className="text-[11px] text-zinc-500 font-mono">
            {section.content.length}/280
          </span>
        )}
        {isOverXLimit && (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">
            Needs trimming
          </Badge>
        )}
      </div>

      <pre className="text-xs text-zinc-300 whitespace-pre-wrap leading-relaxed bg-zinc-900/40 rounded p-2 max-h-40 overflow-y-auto">
        {section.content}
      </pre>

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs px-2 text-zinc-400 hover:text-zinc-100"
          onClick={copy}
        >
          <ClipboardCopy className="w-3 h-3 mr-1" />
          {copied ? "Copied!" : "Copy"}
        </Button>

        {isOverXLimit && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs px-2 text-amber-400 hover:text-amber-300"
            onClick={onTrim}
            disabled={trimming}
          >
            {trimming ? (
              <Loader2 className="w-3 h-3 animate-spin mr-1" />
            ) : null}
            Trim to 280
          </Button>
        )}

        {!isEmail && (
          <Button
            size="sm"
            className="h-7 text-xs px-2 bg-violet-600 hover:bg-violet-500 text-white"
            onClick={onPublish}
          >
            <Send className="w-3 h-3 mr-1" />
            {isGoogleAds ? "Copy to Composer" : "Publish Now"}
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Post History tab ───────────────────────────────────────────────────────────

function PostHistoryTab({ history }: { history: HistoryEntry[] }) {
  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-zinc-500">
        <Send className="w-8 h-8 opacity-30" />
        <p className="text-sm">No posts sent this session.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-zinc-700/50 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-700 hover:bg-transparent">
            <TableHead className="text-zinc-400 text-xs w-40">
              Timestamp
            </TableHead>
            <TableHead className="text-zinc-400 text-xs w-32">
              Platform
            </TableHead>
            <TableHead className="text-zinc-400 text-xs">Preview</TableHead>
            <TableHead className="text-zinc-400 text-xs w-20 text-center">
              Status
            </TableHead>
            <TableHead className="text-zinc-400 text-xs w-28">
              Post ID / Link
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => (
            <TableRow
              key={entry.id}
              className="border-zinc-700/50 hover:bg-zinc-800/30"
            >
              <TableCell className="text-xs text-zinc-400 whitespace-nowrap">
                {entry.ts.toLocaleTimeString()}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5">
                  <PlatformIcon id={entry.platform} className="w-5 h-5 rounded-sm" />
                  <span className="text-xs text-zinc-300">{entry.label}</span>
                </div>
              </TableCell>
              <TableCell className="text-xs text-zinc-400 max-w-xs truncate">
                {entry.preview}
              </TableCell>
              <TableCell className="text-center">
                {entry.ok ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mx-auto" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400 mx-auto" />
                )}
              </TableCell>
              <TableCell className="text-xs">
                {entry.postUrl ? (
                  <a
                    href={entry.postUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-violet-400 underline underline-offset-2 hover:text-violet-300 truncate block max-w-[100px]"
                  >
                    View
                  </a>
                ) : entry.postId ? (
                  <span className="text-zinc-500 font-mono">{entry.postId}</span>
                ) : (
                  <span className="text-zinc-600">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Root SocialPage ─────────────────────────────────────────────────────────────

export function SocialPage() {
  const [channels, setChannels] = useState<ChannelStatus[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeTab, setActiveTab] = useState("compose");

  // For pre-filling the Quick Compose tab from Campaign Posts
  const [prefillContent, setPrefillContent] = useState("");
  const [prefillPlatform, setPrefillPlatform] = useState<PlatformId | null>(null);

  const fetchChannels = useCallback(async () => {
    setChannelsLoading(true);
    try {
      const res = await fetch("/api/social/channels");
      if (!res.ok) throw new Error("Failed to fetch channels");
      const data: unknown = await res.json();
      setChannels(data as ChannelStatus[]);
    } catch {
      // Fallback: show all channels as disconnected
      setChannels(
        PLATFORMS.map((p) => ({
          platform: p.id,
          label: p.label,
          connected: false,
          missing_keys: p.credFields.map((f) => f.key),
        }))
      );
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchChannels();
  }, [fetchChannels]);

  const connectedChannels = new Set<PlatformId>(
    channels.filter((c) => c.connected).map((c) => c.platform)
  );

  const handleSendToCompose = (content: string, platform: PlatformId) => {
    setPrefillContent(content);
    setPrefillPlatform(platform);
    setActiveTab("compose");
  };

  const handleConsumedInitial = () => {
    setPrefillContent("");
    setPrefillPlatform(null);
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950 text-zinc-100">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-violet-600/20 border border-violet-500/30">
          <Send className="w-4 h-4 text-violet-400" />
        </div>
        <div>
          <h1 className="text-base font-semibold text-zinc-100">
            Social Media Hub
          </h1>
          <p className="text-xs text-zinc-500">
            Connect channels, compose posts, publish campaign content
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Badge
            className={cn(
              "text-xs",
              connectedChannels.size > 0
                ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30"
                : "bg-zinc-700/50 text-zinc-400 border-zinc-600/30"
            )}
          >
            {connectedChannels.size} / {PLATFORMS.length} connected
          </Badge>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — channels */}
        <aside className="w-80 flex-shrink-0 border-r border-zinc-800 p-4 overflow-y-auto">
          <ChannelPanel
            channels={channels}
            loading={channelsLoading}
            onRefresh={fetchChannels}
          />
        </aside>

        {/* Right panel — tabbed workspace */}
        <main className="flex-1 overflow-y-auto p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-800 border border-zinc-700 mb-6">
              <TabsTrigger
                value="compose"
                className="text-xs data-[state=active]:bg-zinc-700 data-[state=active]:text-zinc-100 text-zinc-400"
              >
                Quick Compose
              </TabsTrigger>
              <TabsTrigger
                value="campaign"
                className="text-xs data-[state=active]:bg-zinc-700 data-[state=active]:text-zinc-100 text-zinc-400"
              >
                Campaign Posts
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="text-xs data-[state=active]:bg-zinc-700 data-[state=active]:text-zinc-100 text-zinc-400"
              >
                Post History
                {history.length > 0 && (
                  <Badge className="ml-1.5 bg-zinc-600 text-zinc-300 text-[10px] px-1 py-0 border-zinc-500">
                    {history.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="compose" className="mt-0">
              <QuickComposeTab
                connectedChannels={connectedChannels}
                onPublished={(entries) =>
                  setHistory((prev) => [...entries, ...prev])
                }
                initialContent={prefillContent}
                initialPlatform={prefillPlatform}
                onConsumedInitial={handleConsumedInitial}
              />
            </TabsContent>

            <TabsContent value="campaign" className="mt-0">
              <CampaignPostsTab onSendToCompose={handleSendToCompose} />
            </TabsContent>

            <TabsContent value="history" className="mt-0">
              <PostHistoryTab history={history} />
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
}
